import { Component } from '@theme/component';

/** The visitor asked the browser to save data (Chrome's "Lite mode", Android data saver). */
function savesData() {
  const connection = /** @type {{ connection?: { saveData?: boolean } }} */ (navigator).connection;
  return Boolean(connection?.saveData);
}

/**
 * Muted looping clip (blocks/detail-clip.liquid). Plays while at least half of it is on screen and pauses once it
 * leaves; clips scrolled out of a carousel count as off screen, so only the visible ones download. The Liquid sources
 * (`snippets/video-sources.liquid`) are the 480p MP4 on every screen, which fills a ~250-310 px tile. A screen before
 * the carousel scrolls in, every clip in it sets its poster from `data-poster` (also the ones still off to the side,
 * so a swipe never lands on an empty tile); a screen before a clip itself scrolls in it switches from
 * `preload="none"` to `metadata`, so it starts the moment it arrives. The toggle button pauses or resumes; a clip the
 * visitor paused stays paused. With Save-Data only the poster loads; the video waits until the visitor taps play.
 *
 * @typedef {{ video: HTMLVideoElement, toggle: HTMLButtonElement }} Refs
 * @extends {Component<Refs>}
 */
class DetailClipComponent extends Component {
  requiredRefs = ['video', 'toggle'];

  #inView = false;
  #wantsPlay = !savesData();

  #visibility = new IntersectionObserver(
    ([entry]) => {
      this.#inView = Boolean(entry?.isIntersecting);
      this.#sync();
    },
    { threshold: 0.5 }
  );

  #posterApproach = new IntersectionObserver(
    ([entry]) => {
      if (!entry?.isIntersecting) return;
      this.#posterApproach.disconnect();
      this.#showPoster();
    },
    { rootMargin: '100% 0px' }
  );

  #approach = new IntersectionObserver(
    ([entry]) => {
      if (!entry?.isIntersecting) return;
      this.#approach.disconnect();
      const { video } = this.refs;
      if (this.#wantsPlay && !savesData() && video.preload === 'none') video.preload = 'metadata';
    },
    { rootMargin: '100% 0px' }
  );

  connectedCallback() {
    super.connectedCallback();

    this.#visibility.observe(this);
    this.#approach.observe(this);
    this.#posterApproach.observe(this.closest('slideshow-component') ?? this);
    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#visibility.disconnect();
    this.#approach.disconnect();
    this.#posterApproach.disconnect();
  }

  #showPoster() {
    const { video } = this.refs;
    if (video.dataset.poster && video.getAttribute('poster') !== video.dataset.poster) video.poster = video.dataset.poster;
  }

  toggle() {
    this.#wantsPlay = !this.#wantsPlay;
    this.#sync();
  }

  #sync() {
    const { video } = this.refs;
    if (this.#wantsPlay && this.#inView) {
      if (video.paused) {
        // NotAllowedError = the browser declines on autoplay-policy grounds; show the play button instead.
        // An AbortError only means a pause() (scrolled away) interrupted the start.
        video.play().catch((error) => {
          if (error?.name !== 'NotAllowedError') return;
          this.#wantsPlay = false;
          this.#render();
        });
      }
    } else if (!video.paused) {
      video.pause();
    }
    this.#render();
  }

  #render() {
    const { toggle } = this.refs;
    this.toggleAttribute('data-paused', !this.#wantsPlay);
    toggle.setAttribute('aria-label', (this.#wantsPlay ? toggle.dataset.labelPause : toggle.dataset.labelPlay) ?? '');
  }
}

if (!customElements.get('detail-clip-component')) {
  customElements.define('detail-clip-component', DetailClipComponent);
}
